/**
 * Engine-format → editor-format for dispatch strategies.
 *
 * The dispatch engine's evaluator reads its own vocabulary (trigger.type TIME /
 * SOC / PRICE with flat operator / value / for_intervals, time_range,
 * action.command_type, action.target_power_pct, rule.constraints) and also
 * translates what this editor writes (SCHEDULE / GROUP_STATE, condition,
 * lookback_intervals, action.intent, action.constraints). The translation only
 * ran one way, so a strategy written by hand in the engine's words — the EMC
 * Sungrow schedules, the seeded price strategies — opened in the editor with
 * every field blank and the SOC sliders on their defaults.
 *
 * These helpers run on load and map engine fields onto the editor's. Saving
 * writes the editor's format, which the evaluator already understands, so a
 * round trip changes the spelling, not the behaviour. Editor-format input is
 * returned unchanged (idempotent). Unknown keys are kept.
 */

const clone = (v) => JSON.parse(JSON.stringify(v));
const has = (v) => v !== undefined && v !== null;
/** Engine command types the evaluator turns back into from an editor intent. */
const EDITOR_INTENTS = new Set(["IDLE", "CHARGE", "DISCHARGE"]);

/** Map one rule. Pure. */
export function toEditorRule(rule) {
  if (!rule || typeof rule !== "object") return rule;
  const r = clone(rule);
  const t = r.trigger && typeof r.trigger === "object" ? r.trigger : {};
  const a = r.action && typeof r.action === "object" ? r.action : {};
  const type = String(t.type || "").toUpperCase();

  if (type === "TIME") {
    const s = t.schedule && typeof t.schedule === "object" ? t.schedule : {};
    t.type = "SCHEDULE";
    t.schedule = {
      ...s,
      start_time: has(s.start_time) ? s.start_time : (t.time_range?.start ?? null),
      end_time: has(s.end_time) ? s.end_time : (t.time_range?.end ?? null),
      days: Array.isArray(s.days) ? s.days : (Array.isArray(t.day_of_week) ? t.day_of_week : []),
    };
    delete t.time_range;
    delete t.day_of_week;
  } else if (type === "SOC") {
    // Engine SOC trigger: {operator, value} or {min_soc_pct/min, max_soc_pct/max}.
    // The editor only has SOC_ABOVE / SOC_BELOW with one value.
    const g = t.group_state && typeof t.group_state === "object" ? t.group_state : {};
    let condition = g.condition;
    let value = g.value;
    if (!has(condition)) {
      const op = String(t.operator || "");
      if (has(t.value) && (op === ">" || op === ">=")) { condition = "SOC_ABOVE"; value = t.value; }
      else if (has(t.value) && (op === "<" || op === "<=")) { condition = "SOC_BELOW"; value = t.value; }
      else if (has(t.min_soc_pct ?? t.min)) { condition = "SOC_ABOVE"; value = t.min_soc_pct ?? t.min; }
      else if (has(t.max_soc_pct ?? t.max)) { condition = "SOC_BELOW"; value = t.max_soc_pct ?? t.max; }
    }
    t.type = "GROUP_STATE";
    t.group_state = { ...g, condition: condition ?? null, value: value ?? null };
    for (const k of ["operator", "value", "min_soc_pct", "max_soc_pct", "min", "max"]) delete t[k];
  } else if (type === "PRICE") {
    const c = t.condition && typeof t.condition === "object" ? t.condition : {};
    t.condition = {
      ...c,
      operator: has(c.operator) ? c.operator : (t.operator ?? ">"),
      value: has(c.value) ? c.value : (t.value ?? null),
    };
    const maxValue = has(c.max_value) ? c.max_value : t.max_value;
    if (has(maxValue)) t.condition.max_value = maxValue;
    t.lookback_intervals = has(t.lookback_intervals) ? t.lookback_intervals : (t.for_intervals ?? 1);
    if (!has(t.price_source) && String(t.signal || "RRP").toUpperCase() === "RRP") t.price_source = "SPOT_RRP";
    for (const k of ["operator", "value", "max_value", "for_intervals"]) delete t[k];
  }

  // Action: intent from command_type / type, but only for the commands the
  // evaluator maps back from an intent. Anything else (FCAS_RESPONSE,
  // FCAS_RAISE…) keeps its engine spelling, which the evaluator reads first;
  // as an intent it would map to nothing and the rule would run IDLE.
  const cmd = a.command_type ?? a.type;
  const cmdKey = has(cmd) ? String(cmd).toUpperCase() : null;
  if (cmdKey === null || EDITOR_INTENTS.has(cmdKey)) {
    if (!has(a.intent) && cmdKey) a.intent = cmdKey;
    delete a.command_type;
    delete a.type;
  }
  if (!has(a.target_power_kw) && has(a.power_kw)) a.target_power_kw = a.power_kw;
  if (!has(a.target_power_pct) && has(a.power_pct)) a.target_power_pct = a.power_pct;
  delete a.power_kw;
  delete a.power_pct;

  // Constraints: the engine reads rule.constraints first, the editor shows
  // action.constraints. Rule-level wins when both exist, as at runtime.
  if (r.constraints && typeof r.constraints === "object") {
    a.constraints = { ...(a.constraints || {}), ...r.constraints };
    delete r.constraints;
  }

  r.trigger = t;
  r.action = a;
  return r;
}

/** Map a whole dispatch strategy config. Pure. */
export function toEditorConfig(config) {
  if (!config || typeof config !== "object") return config;
  const out = clone(config);
  if (!out.timezone) out.timezone = "Australia/Brisbane";
  if (![5, 15, 30].includes(out.evaluation_interval_minutes)) out.evaluation_interval_minutes = 5;
  out.rules = Array.isArray(out.rules) ? out.rules.map(toEditorRule) : [];
  return out;
}

/** True when a rule carries no SOC gate at all (neither level). */
export function hasSocConstraint(rule) {
  const c = rule?.action?.constraints;
  return !!c && (has(c.min_soc_pct) || has(c.max_soc_pct));
}
